import { useState } from 'react'

import copy from '../../demo/fixtures/w5/copy.json'
import { useDemoStore } from '../../demo/store'
import { Button, Modal } from '../../ui'
import { ProcessSteps } from '../analysis/ProcessSteps'
import { fillTemplate, findDuplicateRule, useW5Live, type W5Live } from '../analysis/live'
import { useProcessRun } from '../analysis/useProcessRun'
import styles from './AutomationPage.module.css'

const candidates: Record<string, { name: string; conditions: string[]; actions: string[] }> = {
  止损: { name: '花费止损', conditions: ['每单成本 > 目标 × 1.3，连续 3 天', '广告回报 < 2.5，连续 3 天'], actions: ['暂停该广告，并通知我', '把预算降到当前上限的 80%'] },
  放量: { name: '稳健放量', conditions: ['广告回报 ≥ 4.0 且预算消耗 ≥ 80%', '每单成本连续 5 天低于目标'], actions: ['日预算上限 +20%（单次变动不超过 20%）', '向高回报广告倾斜 15% 预算'] },
  素材: { name: '素材轮换', conditions: ['素材点击率 < 账户基准 × 0.6，连续 3 天', '同一素材近 7 天频次 > 3.0'], actions: ['换成 AI 素材库里评分最高的候选', '生成两条同方向新素材并择优投放'] },
  受众: { name: '受众扩展', conditions: ['近 7 天新增触达增速低于 5%', '投放期内频次 > 3.0'], actions: ['扩展相似受众 1%', '排除近 7 天已触达 ≥3 次的人群'] },
  合规: { name: '合规复核', conditions: ['素材提交前命中中风险词', '新素材进入审核队列'], actions: ['拦截提交并发起复核', '只提交通过复核的素材'] },
  时段: { name: '高效时段', conditions: ['某时段转化率高于日均 25%，连续 7 天', '夜间每单成本低于日均 15%'], actions: ['预算向该时段倾斜（±15% 以内）', '把低效时段预算转到高效时段'] },
}
const scopes = ['全部任务', '全部销售型任务', '全部曝光型任务']

interface NewRuleDialogProps {
  open: boolean
  groupOrder: W5Live['automation']['groupOrder']
  onClose: () => void
  onDone: (message: string) => void
}

export function NewRuleDialog({ open, groupOrder, onClose, onDone }: NewRuleDialogProps) {
  const addRule = useDemoStore((state) => state.addCustomRule)
  const { automation } = useW5Live()
  const firstPurpose = groupOrder[0] ?? '止损'
  const [purpose, setPurpose] = useState(firstPurpose)
  const [condition, setCondition] = useState(candidates[firstPurpose]?.conditions[0] ?? '')
  const [action, setAction] = useState(candidates[firstPurpose]?.actions[0] ?? '')
  const [scope, setScope] = useState(scopes[0]!)
  const [needsConfirm, setNeedsConfirm] = useState(false)
  const [conflict, setConflict] = useState<string | null>(null)
  const { visibleSteps, running, run } = useProcessRun()
  const options = candidates[purpose] ?? candidates['止损']!

  const changeCondition = () => {
    const currentIndex = options.conditions.indexOf(condition)
    setCondition(options.conditions[(currentIndex + 1) % options.conditions.length] ?? condition)
    setConflict(null)
  }

  const createRule = async () => {
    if (conflict) { changeCondition(); return }
    const candidate = { name: options.name, purpose, cond: condition, action, scope, needsConfirm }
    const duplicate = findDuplicateRule(automation.rules, candidate)
    const guard = (action.includes('预算') || action.includes('出价'))
      ? automation.guards.find((item) => item.includes('预算 / 出价'))
      : action.includes('素材') ? automation.guards.find((item) => item.includes('最多调整 4 条广告'))
        : automation.guards.find((item) => item.includes('24 小时内'))
    const dedupe = duplicate ? `与 ${duplicate.key} ${duplicate.name} 完全重复` : '未发现同条件规则'
    const vars = { cond: condition, action, guard: guard ?? '同一对象 24 小时内不重复调整', ruleCount: automation.rules.length, dedupe }
    const completed = await run(copy.process.newRule.map((step) => ({ label: fillTemplate(step.label, vars), detail: fillTemplate(step.detail, vars) })), copy.process)
    if (!completed) return
    if (duplicate) {
      setConflict(`${duplicate.key} ${duplicate.name} 已经在管这件事——同样的条件、同样的动作、同样的生效范围。换一个条件，或把生效范围改窄一点。`)
      return
    }
    const key = addRule(candidate)
    onDone(`已新建规则 ${key} · ${options.name}：当 ${condition} → ${action}（${scope}），明天 09:00 起生效`)
    onClose()
  }

  const choice = (value: string, picked: boolean, onClick: () => void) => <Button key={value} aria-pressed={picked} className={`${styles.choiceButton} ${picked ? styles.choicePicked : ''}`} disabled={running} variant="ghost" onClick={() => { setConflict(null); onClick() }}>{value}</Button>

  return (
    <Modal open={open} title="新建自动规则" hint="从候选项组合规则，AI 会在每天 09:00 的检查里执行" onClose={running ? () => undefined : onClose} footer={<><Button variant="ghost" disabled={running} onClick={onClose}>取消</Button><Button disabled={running} onClick={() => void createRule()}>{running ? '正在查重' : conflict ? '换一个条件' : '创建规则'}</Button></>}>
      <div className={styles.ruleForm}>
        <section><p>目的</p><div>{groupOrder.map((value) => choice(value, value === purpose, () => { const next = candidates[value] ?? candidates['止损']!; setPurpose(value); setCondition(next.conditions[0] ?? ''); setAction(next.actions[0] ?? '') }))}</div></section>
        <section><p>条件</p><div>{options.conditions.map((value) => choice(value, value === condition, () => setCondition(value)))}</div></section>
        <section><p>动作</p><div>{options.actions.map((value) => choice(value, value === action, () => setAction(value)))}</div></section>
        <section><p>生效范围</p><div>{scopes.map((value) => choice(value, value === scope, () => setScope(value)))}</div></section>
        <section className={styles.confirmRow}><div><p>需要我确认</p><span>开启后，规则命中只生成提案，不直接执行。</span></div><Button role="switch" aria-checked={needsConfirm} aria-label={`需要我确认，${needsConfirm ? '已开启' : '已关闭'}`} className={`${styles.switchButton} ${needsConfirm ? styles.switchOn : ''}`} disabled={running} variant="ghost" onClick={() => { setConflict(null); setNeedsConfirm((value) => !value) }}><i /></Button></section>
        {running ? <div className={styles.processSlot}><ProcessSteps steps={visibleSteps} /></div> : null}
        {conflict ? <div className={styles.conflictCallout}>{conflict}</div> : null}
      </div>
    </Modal>
  )
}
